import { ButtonProps, LinkButtonSettings } from '@almbrand/button';
import { CtaGroup } from '@almbrand/ctagroup';
import { RichText } from '@almbrand/richtext';
import classNames from 'classnames';
import { ctaGroupMargins } from 'common/buttonUtils';
import { useEffect, useRef, useState } from 'react';
import './ParagraphComponent.scss';

export interface ParagraphComponentProps extends LocalId {
	contentId?: number | string;
	backgroundColor?: BackgroundColor;
	padding?: string;
	richText: string;
	contentText?: string; // hidden text
	buttons?: Button[];
	ctaMarginTop?: string;
	ctaMarginBottom?: string;
}

export const ParagraphComponent: React.FC<ParagraphComponentProps> = ({
	contentId,
	backgroundColor,
	padding,
	richText,
	contentText,
	buttons,
	ctaMarginTop,
	ctaMarginBottom,
	useReadline,
	readlineId,
}) => {
	const [isContentTextVisible, setIsContentTextVisible] = useState(false);
	const [fadeColor, setFadeColor] = useState<string>('');
	const hiddenContentRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);

	const rootUrl = globalThis.location.origin;

	// Get background color for fade effect
	useEffect(() => {
		if (wrapperRef.current) {
			const divColor = getComputedStyle(wrapperRef.current).backgroundColor;
			const color = divColor === 'rgba(0, 0, 0, 0)' ? '#fff' : divColor;
			setFadeColor(color);
		}
	}, []);

	// Handle hidden content animation
	useEffect(() => {
		if (hiddenContentRef.current) {
			hiddenContentRef.current.style.maxHeight = isContentTextVisible
				? `${hiddenContentRef.current.scrollHeight}px`
				: '0';
		}
	}, [isContentTextVisible]);

	const toggleContentTextVisibility = () => {
		setIsContentTextVisible(!isContentTextVisible);
	};

	const mapButtonsToCtaGroup = (buttons: Button[]): ButtonProps[] =>
		buttons.map(({ title = '', type, color, link, target }) => ({
			children: title,
			buttonType: (type === 'cta' ? color : type) as ButtonProps['buttonType'],
			linkButtonSettings: {
				title,
				href: link?.url,
				target: target as LinkButtonSettings['target'],
			},
			trackingEvent: {
				event: 'trackNavigation',
				navigationAction: title ?? '',
				navigationDestination: `${rootUrl}${link?.url ?? ''}`,
				navigationType: 'call-to-action',
			},
		}));

	const shouldShowFade = !isContentTextVisible && contentText;

	return (
		<div
			className={classNames('paragraph', backgroundColor?.cssClass, `p-${padding}`)}
			ref={wrapperRef}
			style={{ '--blockBgColor': shouldShowFade ? fadeColor : '' } as React.CSSProperties}
		>
			<div className={classNames({ 'paragraph__add-fade': shouldShowFade })}>
				<RichText
					id={useReadline && readlineId ? readlineId : contentId}
					description={richText}
				/>
			</div>
			{buttons && (
				<CtaGroup
					ctaGroup={mapButtonsToCtaGroup(buttons)}
					className={ctaGroupMargins(ctaMarginTop, ctaMarginBottom)}
				/>
			)}
			{/* Hidden text */}
			{isContentTextVisible && contentText && (
				<div
					className='paragraph__contentText'
					ref={hiddenContentRef}
				>
					<RichText description={contentText} />
				</div>
			)}

			{contentText && (
				<div
					className={classNames(
						'paragraph__contentVisible--triggerWrapper',
						isContentTextVisible && 'paragraph__contentVisible--active'
					)}
				>
					<hr />
					<button onClick={toggleContentTextVisibility}>
						<div className='paragraph__contentVisible--triggerIcon'>
							<span></span>
							<span></span>
						</div>
					</button>
					<hr />
				</div>
			)}
		</div>
	);
};
